import { useCallback, useEffect, useState } from 'react';
import type { AuditLog } from '@kome/shared';
import TopBar from '@/components/TopBar';
import Card from '@/components/Card';
import Badge from '@/components/Badge';
import Button from '@/components/Button';
import DataTable from '@/components/DataTable';
import type { Column } from '@/components/DataTable';
import { adminApi, AdminApiError } from '@/lib/api';

/** 한 번에 불러오는 로그 수 */
const PAGE_SIZE = 50;

/** DataTable 행 타입 (인덱스 시그니처 보장) */
type AuditRow = AuditLog & Record<string, unknown>;

/** 감사 로그 시각: 정확한 날짜 + 시:분:초 (감사용이라 상대표기 대신 절대시각) */
function formatAuditTime(ts: number): string {
  const d = new Date(ts);
  const p = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(
    d.getMinutes(),
  )}:${p(d.getSeconds())}`;
}

/** action 종류에 따른 배지 톤 (생성=성공, 삭제/비활성=위험, 재설정=경고) */
function actionTone(action: string): 'neutral' | 'success' | 'warning' | 'danger' | 'info' {
  const a = action.toLowerCase();
  if (/(create|add|login|invite)/.test(a)) return 'success';
  if (/(delete|remove|deactivate|disable|reset)/.test(a)) return 'danger';
  if (/(update|patch|edit|change)/.test(a)) return 'warning';
  return 'info';
}

/**
 * 감사 로그 페이지 (admin_audit).
 * - api.audit() 로 시간 역순(서버 정렬) 조회.
 * - 시각 / actor / action / target / detail 컬럼.
 * - offset 기반 페이지네이션 ("이전/다음").
 */
export default function Audit() {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  /** 마지막 응답이 가득 찼는지 → 다음 페이지 존재 추정 */
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (nextOffset: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.audit({ limit: PAGE_SIZE, offset: nextOffset });
      // 서버가 시간 역순으로 내려주지만, 방어적으로 createdAt 내림차순 정렬을 한 번 더 적용.
      const logs = [...res.logs].sort((a, b) => b.createdAt - a.createdAt) as AuditRow[];
      setRows(logs);
      setHasMore(res.logs.length >= PAGE_SIZE);
      setOffset(nextOffset);
    } catch (e) {
      const msg = e instanceof AdminApiError ? e.message : '감사 로그를 불러오지 못했습니다.';
      setError(msg);
      setRows([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(0);
  }, [load]);

  const columns: Column<AuditRow>[] = [
    {
      key: 'createdAt',
      header: '시각',
      width: 170,
      render: (row) => <span className="muted">{formatAuditTime(row.createdAt)}</span>,
    },
    {
      key: 'actor',
      header: '주체(actor)',
      width: 160,
      render: (row) => row.actor || <span className="muted">시스템</span>,
    },
    {
      key: 'action',
      header: '행위(action)',
      width: 150,
      render: (row) => <Badge tone={actionTone(row.action)}>{row.action}</Badge>,
    },
    {
      key: 'target',
      header: '대상(target)',
      width: 200,
      render: (row) => row.target || <span className="muted">—</span>,
    },
    {
      key: 'detail',
      header: '상세(detail)',
      sortable: false,
      render: (row) =>
        row.detail ? (
          <span title={row.detail} className="audit-detail">
            {row.detail}
          </span>
        ) : (
          <span className="muted">—</span>
        ),
    },
  ];

  const page = Math.floor(offset / PAGE_SIZE);
  const fromN = rows.length === 0 ? 0 : offset + 1;
  const toN = offset + rows.length;

  return (
    <>
      <TopBar
        title="감사 로그"
        subtitle="관리자 행위 기록 (admin_audit) · 최신순"
        actions={
          <Button size="sm" onClick={() => void load(offset)} loading={loading}>
            새로고침
          </Button>
        }
      />
      <div className="admin-content">
        {error && (
          <Card className="audit-error">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span style={{ color: 'var(--color-badge-red)' }}>{error}</span>
              <Button size="sm" variant="primary" onClick={() => void load(offset)}>
                다시 시도
              </Button>
            </div>
          </Card>
        )}

        <Card title="기록" noPadding>
          <DataTable<AuditRow>
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            loading={loading}
            emptyText="감사 로그가 없습니다."
          />
        </Card>

        <div className="page-toolbar" style={{ marginTop: 'var(--space-lg)', marginBottom: 0 }}>
          <span className="muted">
            {rows.length === 0 ? '0건' : `${fromN}–${toN}번째 (페이지 ${page + 1})`}
          </span>
          <div className="row">
            <Button
              size="sm"
              disabled={offset <= 0 || loading}
              onClick={() => void load(Math.max(0, offset - PAGE_SIZE))}
            >
              이전
            </Button>
            <Button
              size="sm"
              disabled={!hasMore || loading}
              onClick={() => void load(offset + PAGE_SIZE)}
            >
              다음
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
